/**
 * Vincent (2026-02) — Persistance partagée pour l'édition d'une vidéo.
 *
 * Deux surfaces d'édition ont émergé dans l'app :
 *   1. FileExplorerPage — formulaire inline ouvert par le crayon dans la
 *      ligne vidéo (title/module/tags/catégorie/parcours).
 *   2. VideoDetailPage (VideoDetailHeader) — formulaire compact dans le
 *      header (title/dropbox/catégorie/parcours + theme/access_level legacy).
 *
 * Avant, chacune dupliquait la logique de sauvegarde côté API. Ce module
 * centralise les DEUX opérations non-triviales :
 *   • Catégorie      → PUT /api/videos/{id}/category (idempotent, appelé
 *                       uniquement si la valeur a changé).
 *   • Parcours (diff) → PATCH /api/pathways/{id} pour chaque parcours ajouté
 *                       ou retiré, calcul du diff via 2 Set<string>.
 *
 * Les UIs restent distinctes (inline vs popover) car leurs contraintes
 * visuelles diffèrent, mais toute vue peut désormais appeler la même API
 * de persistance sans copier-coller. Toute future divergence de logique
 * (ex : audit trail, optimistic UI) se fait ici en un seul endroit.
 */
import axios from "axios";

/**
 * Sauvegarde la catégorie d'une vidéo si (et seulement si) elle a changé.
 *
 * @param {string} api        Base URL (ex: REACT_APP_BACKEND_URL)
 * @param {string} videoId
 * @param {string} prevCatId  Ancienne category_id (vide/null si aucune)
 * @param {string} newCatId   Nouvelle category_id (vide/null si aucune)
 * @returns {Promise<boolean>} true si un appel réseau a été effectué.
 */
export async function saveCategoryIfChanged(api, videoId, prevCatId, newCatId) {
  const prev = prevCatId || "";
  const next = newCatId || "";
  if (prev === next) return false;
  await axios.put(
    `${api}/api/videos/${videoId}/category`,
    { category_id: next || null },
    { withCredentials: true },
  );
  return true;
}

/**
 * Calcule le diff entre les parcours associés AVANT et APRÈS édition, puis
 * envoie 1 PATCH par parcours ajouté/retiré. Chaque PATCH remplace le tableau
 * complet `video_ids` du parcours (endpoint idempotent — safe si retry).
 *
 * @param {string} api
 * @param {string} videoId
 * @param {Array<{id: string, video_ids?: string[]}>} allPathways
 * @param {Set<string>} prevIds  Snapshot des parcours associés avant édition
 * @param {Set<string>} nextIds  Sélection actuelle dans le formulaire
 * @returns {Promise<{added: number, removed: number}>} Compteurs mutations.
 */
export async function savePathwayDiff(api, videoId, allPathways, prevIds, nextIds) {
  const prev = prevIds instanceof Set ? prevIds : new Set(prevIds || []);
  const next = nextIds instanceof Set ? nextIds : new Set(nextIds || []);
  const toAdd = [...next].filter((pid) => !prev.has(pid));
  const toRemove = [...prev].filter((pid) => !next.has(pid));
  const paths = allPathways || [];
  for (const pid of toAdd) {
    const p = paths.find((x) => x.id === pid);
    if (!p) continue;
    const nextVideoIds = [...(p.video_ids || []), videoId];
    await axios.patch(
      `${api}/api/pathways/${pid}`,
      { video_ids: nextVideoIds },
      { withCredentials: true },
    );
  }
  for (const pid of toRemove) {
    const p = paths.find((x) => x.id === pid);
    if (!p) continue;
    const nextVideoIds = (p.video_ids || []).filter((id) => id !== videoId);
    await axios.patch(
      `${api}/api/pathways/${pid}`,
      { video_ids: nextVideoIds },
      { withCredentials: true },
    );
  }
  return { added: toAdd.length, removed: toRemove.length };
}

/**
 * Snapshot utilitaire : Set des `pathway.id` où la vidéo apparaît dans
 * `video_ids`. Sert à init `editForm.pathway_ids` cohéremment entre les
 * 2 surfaces d'édition.
 */
export function pathwayIdsForVideo(allPathways, videoId) {
  const s = new Set();
  for (const p of allPathways || []) {
    if ((p.video_ids || []).includes(videoId)) s.add(p.id);
  }
  return s;
}
